import { useState, useRef, useEffect, useCallback, KeyboardEvent, ClipboardEvent } from 'react';
import { TextField, Box, Stack, InputLabel } from '@mui/material';
import { Control, Controller, FieldError, Path } from 'react-hook-form';
import { FormErrorText } from '../../other/ErrorText/FormErrorText';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';

interface OTPFieldProps<T extends object> {
    id: string;
    name: Path<T>;
    control: Control<T>;
    length?: number;
    onComplete?: (otp: string) => void | Promise<void>;
    numbersOnly?: boolean;
    label?: number | string;
    gap?: number | string;
    autoFocus?: boolean;
    error?: FieldError;
    required?: boolean;
    disabled?: boolean;
    'data-testid'?: string;
}

export const OTPField = <T extends object>({
    id,
    name,
    control,
    length = 6,
    onComplete,
    numbersOnly,
    label,
    gap = 0.5,
    autoFocus = false,
    error,
    required,
    disabled,
    'data-testid': dataTestId = 'otp-field',
}: OTPFieldProps<T>) => {
    const { t } = usePhrase();
    const [hasOnCompleteBeenCalled, setHasOnCompleteBeenCalled] = useState(false);
    const [otp, setOtp] = useState<string[]>(new Array(length).fill(''));
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const blurInputs = () =>
        // setTimeout required to defer blurring of the input element until it's fully rendered
        setTimeout(() => {
            inputRefs.current.forEach((input) => {
                input?.blur();
            });
        }, 0);

    const selectInputByIndex = useCallback(
        (index: number) => {
            if (index >= 0 && index < length) {
                requestAnimationFrame(() => {
                    const input = inputRefs.current[index];
                    if (input) {
                        input.focus();
                        input.select();
                    }
                });
            }
        },
        [length],
    );

    const updateOTP = useCallback(
        (newOtp: string[], onChange: (value: string) => void) => {
            setOtp(newOtp);
            const otpValue = newOtp.join('');
            onChange(otpValue); // Update form value on every change
            if (otpValue.length === length && onComplete) {
                setHasOnCompleteBeenCalled(true);
                void onComplete(otpValue);
                blurInputs();
            }
        },
        [length, onComplete],
    );

    const isDigit = (char: string): boolean => /^\d$/.test(char);

    const handleInputChange = useCallback(
        (value: string, focusedInputIndex: number, onChange: (value: string) => void) => {
            const newOtp = [...otp];
            let nextEmptyIndex = focusedInputIndex;

            // If the code was submitted clear all values after the current one
            if (hasOnCompleteBeenCalled) {
                newOtp.fill('', nextEmptyIndex);
                setHasOnCompleteBeenCalled(false);
            }

            // If the current field is empty, find the first empty field
            if (newOtp[focusedInputIndex] === '') {
                nextEmptyIndex = newOtp.findIndex((digit) => digit === '');
                if (nextEmptyIndex === -1) {
                    nextEmptyIndex = focusedInputIndex;
                }
            }

            // Process the input value
            for (let i = 0; i < value.length && nextEmptyIndex < length; i++) {
                const char = value[i];
                if (!numbersOnly || isDigit(char)) {
                    newOtp[nextEmptyIndex] = char;
                    nextEmptyIndex++;
                }
            }

            updateOTP(newOtp, onChange);
            selectInputByIndex(Math.min(nextEmptyIndex, length - 1));
        },
        [otp, hasOnCompleteBeenCalled, updateOTP, selectInputByIndex, length, numbersOnly],
    );

    const handleKeyDown = (
        index: number,
        evt: KeyboardEvent<HTMLDivElement>,
        onChange: (value: string) => void,
    ) => {
        const handleClearAction = () => {
            if (otp[index] === '' && index > 0) {
                const newOtp = [...otp];
                newOtp[index - 1] = '';
                updateOTP(newOtp, onChange);
                selectInputByIndex(index - 1);
            } else {
                const newOtp = [...otp];
                newOtp[index] = '';
                updateOTP(newOtp, onChange);
            }
        };
        const handleMoveRightAction = () => selectInputByIndex(Math.min(length - 1, index + 1));
        const handleMoveLeftAction = () => selectInputByIndex(Math.max(0, index - 1));
        const handleMoveToTheBeginningAction = () => selectInputByIndex(0);
        const handleMoveToTheEndAction = () => selectInputByIndex(length - 1);
        const handleLeaveSelectionAction = () => selectInputByIndex(index);

        const keyActions: { [key: string]: () => void } = {
            Backspace: handleClearAction,
            Delete: handleClearAction,
            ArrowUp: handleLeaveSelectionAction,
            ArrowDown: handleLeaveSelectionAction,
            ArrowLeft: handleMoveLeftAction,
            ArrowRight: handleMoveRightAction,
            Home: handleMoveToTheBeginningAction,
            End: handleMoveToTheEndAction,
            ' ': handleMoveRightAction, // Space
        };

        const keysWithDisabledEvtPrevent = ['ArrowUp', 'ArrowDown'];

        if (keyActions[evt.key]) {
            if (!keysWithDisabledEvtPrevent.includes(evt.key)) {
                evt.preventDefault();
            }
            keyActions[evt.key]();
        }

        // If the value hasn't changed, focus on the next field
        // Covers the case when onChange is not fired if the same value entered
        if (evt.key === otp[index]) {
            selectInputByIndex(Math.min(index + 1, length - 1));
        }
    };

    const handlePaste = (
        evt: ClipboardEvent,
        focusedInputIndex: number,
        onChange: (value: string) => void,
    ) => {
        evt.preventDefault();
        const pastedData = evt.clipboardData.getData('text/plain');
        handleInputChange(pastedData, focusedInputIndex, onChange);
    };

    useEffect(() => {
        if (autoFocus) {
            selectInputByIndex(0);
        }
    }, [autoFocus, selectInputByIndex]);

    return (
        <Controller
            name={name}
            control={control}
            rules={{ required: required, validate: (value: string) => value.length === length }}
            render={({ field, fieldState: { error: fieldError } }) => (
                <Stack
                    data-testid={dataTestId}
                    sx={{
                        gap: 2,
                    }}
                >
                    {label && (
                        <InputLabel
                            id={`${id}-label`}
                            htmlFor={`${id}-input-0`}
                            required={required}
                            component="label"
                            sx={{ mb: 1 }}
                        >
                            {typeof label === 'string' ? label : t(label)}
                        </InputLabel>
                    )}
                    <Box
                        role="group"
                        aria-labelledby={label ? `${id}-label` : undefined}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexWrap: ['wrap', 'nowrap'],
                            gap: gap,
                        }}
                    >
                        {otp.map((symbol, index) => (
                            <TextField
                                key={index}
                                id={`${id}-input-${index}`}
                                inputRef={(el: HTMLInputElement | null) => {
                                    if (inputRefs.current) {
                                        inputRefs.current[index] = el;
                                    }
                                }}
                                value={symbol}
                                onChange={(evt) =>
                                    handleInputChange(evt.target.value, index, field.onChange)
                                }
                                onKeyDown={(evt) => handleKeyDown(index, evt, field.onChange)}
                                onPaste={(evt) => handlePaste(evt, index, field.onChange)}
                                onFocus={() => selectInputByIndex(index)}
                                error={Boolean(fieldError || error)}
                                disabled={disabled}
                                className="OTPFieldTextfield"
                                slotProps={{
                                    htmlInput: {
                                        maxLength: 1,
                                        style: { textAlign: 'center' },
                                        inputMode: numbersOnly ? 'numeric' : 'text',
                                        pattern: numbersOnly ? '[0-9]*' : undefined,
                                        'aria-label': `Digit ${index + 1}`,
                                        className: 'OTPFieldInput',
                                    },
                                }}
                            />
                        ))}
                    </Box>
                    <FormErrorText error={fieldError || error} />
                </Stack>
            )}
        />
    );
};
